var m = require('mithril');
var prop = require('mithril/stream');
var moment = require('moment-timezone');
var uuid = require('uuid/v1');

var io = require('../libs/socketio');
var directPointSubscription = require('../components/directPointSubscription');
var modal = require('../layout/components/modal');
var loaders = require('../layout/components/loaders');
var writeCommandEvents = require('../data/writeCommandEvents');
var writeOperations = require('../data/writeOperation');
var Point = require('../data/models/point');
var Calendar = require('../components/calendar');
var eventForm = require('./models/calendarEvent');
var rruleForm = require('./models/rrule');
var pageTitle = require('../layout/pageTitle');

var component = {};

component.oninit = function component () {
  var ctrl = this;
  pageTitle.set(null, { fallback: 'Calendar' });
  ctrl.eventForm = eventForm;
  ctrl.rruleForm = rruleForm;
  ctrl.originColumn = prop({});
  ctrl.calendarPointRecord = prop({ breadcrumbs: [] });
  ctrl.value = prop([]);
  ctrl.writeError = prop(null);
  ctrl.writeOperations = {};
  ctrl.writePromise = null;
  ctrl.writeGeneration = 0;

  ctrl.init = function () {
    return loaders.requestWithLoader({
      loader: 'main',
      url: '/api/v1/point/' + m.route.param('id') + '/',
      method: 'GET',
    })
    .then(function (res) {
      pageTitle.set(res, { fallback: 'Calendar' });
      res.breadcrumbs = [];
      ctrl.calendarPointRecord = prop(res);
      try {
        ctrl.value(typeof res.value === 'string' ? JSON.parse(res.value) : (res.value || []));
      } catch (error) {
        ctrl.value([]);
      }
    });
  }

  ctrl.handleUpdate = function (data) {
    // To prevent conflict with form entry
    if (modal.vm.opened()) { return }
    if (typeof data.value == 'string') {
      ctrl.value(JSON.parse(data.value))  
    } else {
      ctrl.value(data.value)
    }
    ctrl.calendar.refetchEvents()

    m.redraw();
  }

  ctrl.init()
  .then(function () {
    // Subscribe
    directPointSubscription.subscribe(ctrl, m.route.param('id'));
    // Renew every 20 seconds
    ctrl.renewSubscription = setInterval(
      directPointSubscription.renew.bind(directPointSubscription, ctrl, m.route.param('id')),
      20000
    );

    io.on('update', ctrl.handleUpdate);
  })


  if (m.route.param('column')) {
    var url = '/api/v1/column/' + m.route.param('column').slice(1);

    loaders.requestWithLoader({
      loader: 'main',
      url: url,
      method: 'GET'
    })
    .then(ctrl.originColumn);
  }

  // Determine parent record RID used for write permissions
  ctrl.getWriteParentRid = function () {
    if (ctrl.originColumn() && ctrl.originColumn()['@rid']) return ctrl.originColumn()['@rid'];
    if (ctrl.calendarPointRecord() && ctrl.calendarPointRecord().parentEquipment && ctrl.calendarPointRecord().parentEquipment['@rid']) return ctrl.calendarPointRecord().parentEquipment['@rid'];
    return null;
  }

  ctrl.writeCalendarValue = function (candidateValue) {
    var writeGeneration = ctrl.writeGeneration + 1;
    ctrl.writeGeneration = writeGeneration;
    function isCurrentWrite() {
      return ctrl.writeGeneration === writeGeneration;
    }
    function ensureCurrentWrite() {
      if (isCurrentWrite()) return;
      var error = new Error('A newer calendar save replaced this action.');
      error.code = 'BACNET_CALENDAR_WRITE_SUPERSEDED';
      throw error;
    }
    function supersededWrite(error) {
      return { superseded: true, error: error || null };
    }
    const parentRid = ctrl.getWriteParentRid();
    if (!parentRid) {
      const message = 'Cannot update this calendar without a parent location, equipment, or column context.';
      ctrl.writeError(message);
      m.redraw();
      return Promise.reject(new Error(message));
    }

    const sourcePointRid = ctrl.calendarPointRecord()['@rid'];
    const nextValue = JSON.parse(JSON.stringify(
      typeof candidateValue === 'undefined' ? ctrl.value() : candidateValue
    ));
    const url = '/api/v1/point/' + sourcePointRid.slice(1) + '/write/' + parentRid.slice(1);
    var writeOperation = null;
    ctrl.writeError(null);
    var writePromise = writeOperations.prepare(
      ctrl.writeOperations,
      'calendar',
      url,
      {
        value: nextValue,
        commandType: 'schedule',
        sourcePointRid: sourcePointRid,
        expectedPointConfiguration: Point.expectedPointConfiguration(
          ctrl.calendarPointRecord()
        )
      },
      {
        scope: [sourcePointRid, parentRid, 'bacnet-calendar-write'].join(':'),
        intent: {
          commandType: 'schedule',
          sourcePointRid: sourcePointRid,
          value: nextValue
        },
        conflictScopes: [writeOperations.pointConflictScope(sourcePointRid, parentRid)],
        conflictGroup: JSON.stringify({
          surface: 'bacnet-calendar-write',
          sourcePointRid: sourcePointRid,
          parentRid: parentRid,
          value: nextValue
        }),
        recoveryPolicy: writeOperations.ATTENDED_RECOVERY_POLICY
      }
    ).then(function (preparedOperation) {
      writeOperation = preparedOperation;
      if (!isCurrentWrite()) return supersededWrite();
      return writeCommandEvents.waitForPointWrite({
      pointRid: writeOperations.pointRidForOperation(writeOperation),
      request: async function () {
        ensureCurrentWrite();
        await writeOperations.markAttempted(writeOperation);
        ensureCurrentWrite();
        return loaders.requestWithLoader({
          loader: 'submit',
          url: writeOperation.url,
          method: 'PUT',
          body: writeOperation.body
        });
      },
      timeoutMessage: 'Timed out waiting for calendar write confirmation.'
      });
    }).then(async function (completion) {
      if (completion && completion.superseded) return completion;
      if (!isCurrentWrite()) return supersededWrite();
      // The editor continues to show the last confirmed device value while a
      // request is pending or ambiguous. Commit this immutable candidate only
      // after the exact Command has been confirmed.
      await writeOperations.complete(ctrl.writeOperations, 'calendar', writeOperation);
      if (!isCurrentWrite()) return supersededWrite();
      ctrl.value(JSON.parse(JSON.stringify(nextValue)));
      return completion;
    }).catch(async function (error) {
      if (!isCurrentWrite()) return supersededWrite(error);
      if (error && error.writeApplied) {
        ctrl.value(JSON.parse(JSON.stringify(nextValue)));
        ctrl.writeError(null);
        m.redraw();
        return { writeApplied: true };
      }
      if (writeOperation) {
        try {
          await writeOperations.fail(ctrl.writeOperations, 'calendar', writeOperation, error);
        } catch (recoveryError) {
          error = recoveryError;
        }
      }
      if (!isCurrentWrite()) return supersededWrite(error);
      var message = error && (error.error || error.message) || String(error || 'Calendar write failed');
      ctrl.writeError(message);
      m.redraw();
      throw error;
    }).then(function (completion) {
      if (ctrl.writePromise === writePromise) ctrl.writePromise = null;
      return completion;
    }, function (error) {
      if (ctrl.writePromise === writePromise) ctrl.writePromise = null;
      throw error;
    });
    ctrl.writePromise = writePromise;
    return writePromise;
  }

    // events
  ctrl.addEvent = function (calendar, info) {
    modal.vm.title('Create New Event');
    ctrl.eventForm.initForm();
    ctrl.rruleForm.initForm();
    ctrl.eventForm.form.id(uuid())
    ctrl.eventForm.form.start(moment(info.date));
    ctrl.eventForm.form.duration('01:00:00');
    modal.vm.body = m(eventForm);
    modal.vm.buttons([
      {
        label: "Submit",
        class: "dib ",
        func: function () {
          eventForm.form.rrule(rruleForm.toJSON())
          var nextValue = JSON.parse(JSON.stringify(ctrl.value()));
          nextValue.push(eventForm.toJSON.call(eventForm));
          if (ctrl.calendarPointRecord().resource['@class'] == 'BacnetProperty') {
            return ctrl.writeCalendarValue(nextValue)
            .then(function (completion) {
              if (completion && completion.superseded) return completion;
              return ctrl.init();
            })
            .then(function (completion) {
              if (completion && completion.superseded) return completion;
              calendar.refetchEvents();
              modal.vm.close();
              m.redraw();
            })
          }
        }
      }
    ]);
    modal.vm.open();
    m.redraw();
  };

  ctrl.editEvent = function (calendar, info) {
    modal.vm.body = m(eventForm);
    modal.vm.title('Edit Column Name');
    modal.vm.open();
    var matchedIndex = ctrl.value().findIndex(function (el) { return el.id == info.event.id });
    var matchedRecord = ctrl.value()[matchedIndex]

    ctrl.eventForm.initForm(matchedRecord);
    ctrl.eventForm.form.title(info.event.title);
    ctrl.eventForm.form.start(moment(info.event.start));
    ctrl.eventForm.form.duration(matchedRecord.duration);
    ctrl.rruleForm.initForm(matchedRecord.rrule)

    modal.vm.buttons([
      {
        label: "Submit",
        class: "dib ",
        func: function () {
          rruleForm.form.dtstart(eventForm.form.start());
          eventForm.form.rrule(rruleForm.toJSON());
          var nextValue = JSON.parse(JSON.stringify(ctrl.value()));
          nextValue[matchedIndex] = eventForm.toJSON();

          return ctrl.writeCalendarValue(nextValue)
          .then(function (completion) {
            if (completion && completion.superseded) return completion;
            return ctrl.init();
          })
          .then(function (completion) {
            if (completion && completion.superseded) return completion;
            info.event.source.refetch();
            modal.vm.close();
            m.redraw();
          })
        }
      }
    ]);
    m.redraw();
  };
};

component.onremove = function () {
  clearInterval(this.renewSubscription);
  directPointSubscription.release(this);
  io.off('update', this.handleUpdate);
};

component.view = function view () {
  var ctrl = this;
  function heading () {
    if (ctrl.originColumn().path) {
      var dashboardPath = [];
      ctrl.originColumn().path.forEach(function (ancestor, idx) {
        if (ancestor.name === 'root') {
          dashboardPath.push(
            m('div.mr2.dib-ns', [
              m(m.route.Link, {href:'/'}, 'Home'),
              m('span', ' / ')
            ])
          )
        } else if (typeof ancestor.name == 'string' && ancestor.name[0] === '-') {
          return;
        } else if (ancestor['@class'] !== 'Dashboard') {
          dashboardPath.push([
            m('div.mr2.dib-ns', ancestor.name + ' / '),
          ]);
        } else {
          dashboardPath.push(
            m('div.mr2.dib-ns', [
              m(m.route.Link, {href: '/' + ancestor['@id'].split('/').slice(3).join('/')}, ancestor.name),
              m('span', ' / ')
            ])
          )
        }
      })
      return m('div.mb3.pb1.bb.bw1.b--mid-gray.near-white.f-subheadline', dashboardPath);
    } else if (ctrl.calendarPointRecord().breadcrumbs.length > 1) {
      var ancestors = ctrl.calendarPointRecord().breadcrumbs.slice(0, ctrl.calendarPointRecord().breadcrumbs.length - 1);
      return m("div", {class:"container"}, [
        m('div.mb3.bb.bw1.b--mid-gray.near-white.f-subheadline.pb1', [
          ancestors.map(function (ancestor, idx) {
            return m('div.mr2.dib-ns', [
              m(m.route.Link, {href: '/' + ancestor['@id'].split('/').slice(3).join('/')}, ancestor.name),
              ' / '
            ])
          }),
          m('div.mr2.dib-ns', ctrl.calendarPointRecord().name),
        ]),
      ])
    } else {
      return m('div.mb3.pb1.bb.bw1.b--mid-gray.near-white.f-subheadline', ctrl.calendarPointRecord().name)
    }
  }

  return m('div', [
    heading(),
    ctrl.writeError() ? m('div.bg-red.white.pa2.mb2', ctrl.writeError()) : null,
    m(Calendar, ctrl)
  ])
};

module.exports = component;
